import React, { useState, useEffect, FormEvent } from 'react';
import {
    Architect,
    getAllArchitects,
    createArchitect,
    updateArchitect,
    deleteArchitect,
    resendInvitation,
} from '../../Services/ArchitectService';
import {
    Subscription,
    QuotaSnapshot,
    getAllSubscriptions,
    getArchitectSubscription,
    assignArchitectSubscription,
    cancelArchitectSubscription,
} from '../../Services/SubscriptionService';

interface ArchitectFormData {
    firstName: string;
    email: string;
}

const ArchitectManagement: React.FC = () => {
    const [architects, setArchitects] = useState<Architect[]>([]);
    const [snapshots, setSnapshots] = useState<Record<string, QuotaSnapshot>>({});
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingArchitect, setEditingArchitect] = useState<Architect | null>(null);
    const [formData, setFormData] = useState<ArchitectFormData>({ firstName: '', email: '' });
    const [formError, setFormError] = useState<string | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    // Assign-subscription modal state
    const [assignModalArchitect, setAssignModalArchitect] = useState<Architect | null>(null);
    const [assignSubscriptionId, setAssignSubscriptionId] = useState<string>('');
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        loadArchitects();
    }, []);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const loadArchitects = async () => {
        try {
            setLoading(true);
            setError(null);
            const [data, subs] = await Promise.all([
                getAllArchitects(),
                getAllSubscriptions().catch(() => [] as Subscription[]),
            ]);
            setArchitects(data);
            setSubscriptions(subs);
            const snaps: Record<string, QuotaSnapshot> = {};
            await Promise.all(data.map(async (a) => {
                try {
                    snaps[a.userId] = await getArchitectSubscription(a.userId);
                } catch {
                    // ignore individual errors
                }
            }));
            setSnapshots(snaps);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error cargando arquitectos');
        } finally {
            setLoading(false);
        }
    };

    const reloadSnapshot = async (architectId: string) => {
        try {
            const s = await getArchitectSubscription(architectId);
            setSnapshots(prev => ({ ...prev, [architectId]: s }));
        } catch { /* noop */ }
    };

    const openAssignModal = (architect: Architect) => {
        setAssignModalArchitect(architect);
        setAssignSubscriptionId(subscriptions[0]?.subscriptionId ?? '');
        setAssignError(null);
    };

    const closeAssignModal = () => {
        setAssignModalArchitect(null);
        setAssignError(null);
    };

    const handleAssign = async (e: FormEvent) => {
        e.preventDefault();
        if (!assignModalArchitect) return;
        if (!assignSubscriptionId) {
            setAssignError('Selecciona una suscripción.');
            return;
        }
        try {
            setAssignLoading(true);
            await assignArchitectSubscription(assignModalArchitect.userId, assignSubscriptionId);
            setSuccessMessage(`Suscripción asignada a ${assignModalArchitect.firstName}.`);
            await reloadSnapshot(assignModalArchitect.userId);
            closeAssignModal();
        } catch (err: any) {
            setAssignError(err.response?.data?.error || err.message || 'Error asignando suscripción');
        } finally {
            setAssignLoading(false);
        }
    };

    const handleCancelSubscription = async (architect: Architect) => {
        if (!window.confirm(`¿Cancelar la suscripción activa de "${architect.firstName}"?`)) return;
        try {
            setActionLoading(architect.userId);
            await cancelArchitectSubscription(architect.userId);
            setSuccessMessage(`Suscripción cancelada para ${architect.firstName}.`);
            await reloadSnapshot(architect.userId);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error cancelando suscripción');
        } finally {
            setActionLoading(null);
        }
    };

    const renderSubscriptionCell = (architect: Architect) => {
        const snap = snapshots[architect.userId];
        if (!snap) return <span className="text-muted">—</span>;
        if (!snap.plan_name) {
            return <span className="text-muted" title="Plan gratuito">Sin suscripción · {snap.used}/{snap.limit}</span>;
        }
        const expires = snap.expires_at ? new Date(snap.expires_at).toLocaleDateString() : '—';
        return <span title={`Renovación: ${expires}`}><strong>{snap.plan_name}</strong> · {snap.used}/{snap.limit}</span>;
    };

    const openCreateModal = () => {
        setEditingArchitect(null);
        setFormData({ firstName: '', email: '' });
        setFormError(null);
        setShowModal(true);
    };

    const openEditModal = (architect: Architect) => {
        setEditingArchitect(architect);
        setFormData({ firstName: architect.firstName, email: architect.username });
        setFormError(null);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingArchitect(null);
        setFormData({ firstName: '', email: '' });
        setFormError(null);
    };

    const validateForm = (): boolean => {
        if (!formData.firstName.trim()) {
            setFormError('El nombre del despacho es obligatorio.');
            return false;
        }
        if (!formData.email.trim()) {
            setFormError('El email es obligatorio.');
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
            setFormError('El formato del email no es válido.');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setFormError(null);

        if (!validateForm()) return;

        try {
            setFormLoading(true);
            if (editingArchitect) {
                await updateArchitect(editingArchitect.userId, formData.firstName.trim(), formData.email.trim());
                setSuccessMessage('Arquitecto actualizado correctamente.');
            } else {
                await createArchitect(formData.firstName.trim(), formData.email.trim());
                setSuccessMessage('Arquitecto creado correctamente. Se ha enviado un email de invitación.');
            }
            closeModal();
            await loadArchitects();
        } catch (err: any) {
            setFormError(err.response?.data?.error || err.message || 'Error al guardar el arquitecto.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (architect: Architect) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar a "${architect.firstName}"?`)) {
            return;
        }

        try {
            setActionLoading(architect.userId);
            await deleteArchitect(architect.userId);
            setArchitects(architects.filter(a => a.userId !== architect.userId));
            setSuccessMessage('Arquitecto eliminado correctamente.');
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error eliminando arquitecto');
        } finally {
            setActionLoading(null);
        }
    };

    const handleResendInvitation = async (architect: Architect) => {
        try {
            setActionLoading(architect.userId);
            await resendInvitation(architect.userId);
            setSuccessMessage(`Invitación reenviada a ${architect.username}.`);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error reenviando invitación');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Arquitectos</h2>
                <button className="btn btn-primary" onClick={openCreateModal} style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}>
                    + Nuevo Arquitecto
                </button>
            </div>

            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
            )}

            {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {successMessage}
                    <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
                </div>
            )}

            {architects.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <h5 className="text-muted">No hay arquitectos registrados</h5>
                        <p className="text-muted mb-4">
                            Crea un nuevo arquitecto para comenzar
                        </p>
                        <button className="btn btn-primary" onClick={openCreateModal}>
                            + Nuevo Arquitecto
                        </button>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Nombre del despacho</th>
                                <th>Email</th>
                                <th>Suscripción</th>
                                <th className="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {architects.map((architect) => {
                                const snap = snapshots[architect.userId];
                                const hasActive = !!snap?.plan_name;
                                return (
                                <tr key={architect.userId}>
                                    <td>{architect.firstName}</td>
                                    <td>{architect.username}</td>
                                    <td>{renderSubscriptionCell(architect)}</td>
                                    <td className="text-end">
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                className="btn btn-link text-dark text-decoration-none"
                                                onClick={() => openEditModal(architect)}
                                                disabled={actionLoading === architect.userId}
                                                title="Editar"
                                            >
                                                Editar
                                            </button>
                                            {hasActive ? (
                                                <button
                                                    className="btn btn-link text-decoration-none"
                                                    style={{ color: '#f57c00' }}
                                                    onClick={() => handleCancelSubscription(architect)}
                                                    disabled={actionLoading === architect.userId}
                                                    title="Cancelar suscripción"
                                                >
                                                    Cancelar suscripción
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn btn-link text-decoration-none"
                                                    onClick={() => openAssignModal(architect)}
                                                    disabled={actionLoading === architect.userId || subscriptions.length === 0}
                                                    title={subscriptions.length === 0 ? 'No hay suscripciones definidas' : 'Asignar suscripción'}
                                                >
                                                    Asignar suscripción
                                                </button>
                                            )}
                                            {architect.hasPendingInvitation && (
                                                <button
                                                    className="btn btn-link text-decoration-none"
                                                    style={{ color: '#f57c00' }}
                                                    onClick={() => handleResendInvitation(architect)}
                                                    disabled={actionLoading === architect.userId}
                                                    title="Reenviar invitación"
                                                >
                                                    {actionLoading === architect.userId ? (
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    ) : (
                                                        'Reenviar invitación'
                                                    )}
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-link text-danger text-decoration-none"
                                                onClick={() => handleDelete(architect)}
                                                disabled={actionLoading === architect.userId}
                                                title="Eliminar"
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {editingArchitect ? 'Editar Arquitecto' : 'Nuevo Arquitecto'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeModal}
                                        disabled={formLoading}
                                    ></button>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body">
                                        {formError && (
                                            <div className="alert alert-danger">{formError}</div>
                                        )}
                                        <div className="mb-3">
                                            <label htmlFor="firstName" className="form-label">
                                                Nombre del despacho *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="firstName"
                                                value={formData.firstName}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, firstName: e.target.value })
                                                }
                                                disabled={formLoading}
                                                placeholder="Nombre del despacho de arquitectura"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="email" className="form-label">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                value={formData.email}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, email: e.target.value })
                                                }
                                                disabled={formLoading}
                                                placeholder="email@ejemplo.com"
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer d-flex justify-content-end gap-2" style={{ alignItems: 'stretch' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-dark"
                                            onClick={closeModal}
                                            disabled={formLoading}
                                            style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={formLoading}
                                            style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}
                                        >
                                            {formLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Guardando...
                                                </>
                                            ) : editingArchitect ? (
                                                'Guardar cambios'
                                            ) : (
                                                'Crear Arquitecto'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {assignModalArchitect && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Asignar suscripción a {assignModalArchitect.firstName}</h5>
                                    <button type="button" className="btn-close" onClick={closeAssignModal} disabled={assignLoading}></button>
                                </div>
                                <form onSubmit={handleAssign}>
                                    <div className="modal-body">
                                        {assignError && <div className="alert alert-danger">{assignError}</div>}
                                        <div className="mb-3">
                                            <label htmlFor="subscriptionId" className="form-label">Suscripción *</label>
                                            <select
                                                id="subscriptionId"
                                                className="form-select"
                                                value={assignSubscriptionId}
                                                onChange={(e) => setAssignSubscriptionId(e.target.value)}
                                                disabled={assignLoading}
                                            >
                                                {subscriptions.map(s => (
                                                    <option key={s.subscriptionId} value={s.subscriptionId}>
                                                        {s.name} — {s.projectsPerYearLimit} proyectos/año
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                            La asignación dura 12 meses desde hoy.
                                        </p>
                                    </div>
                                    <div className="modal-footer d-flex justify-content-end gap-2" style={{ alignItems: 'stretch' }}>
                                        <button type="button" className="btn btn-outline-dark" onClick={closeAssignModal} disabled={assignLoading} style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}>
                                            Cancelar
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={assignLoading} style={{ minWidth: '160px', padding: '0.375rem 0.75rem' }}>
                                            {assignLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Asignando...
                                                </>
                                            ) : 'Asignar'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ArchitectManagement;
